import Image from "next/image";
import { useEffect, useMemo, useState } from "react";

import { getBannerInfo } from "../config/bannerMapping";

/**
 
 * @param {Object} props - Component props
 * @param {string} props.categoryName - The name of the category
 * @param {string} props.size - Size of the icon (sm, md, lg, xl)
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 * @param {boolean} props.showTooltip - Whether to show tooltip on hover
 * @returns {JSX.Element} Category icon component
 */

const Banner = ({
  categoryName,
  imageUrl,
  iconUrl,
  size = "xl",
  onClick,
  className = "",
  style = {},
  showTooltip = false,
  normalCleaning = true,
}) => {
  const categoryInfo = getBannerInfo(categoryName, normalCleaning);
  const mappedIcon = categoryInfo?.icon;
  const mappedName = categoryInfo?.displayName || categoryName || "Service Banner";

  // Size mapping
  const sizeClasses = {
    sm: "w-20 h-20",
    md: "w-24 h-24",
    lg: "w-28 h-28",
    xl: "w-32 h-32",
  };

  const iconSizeClasses = {
    sm: "w-6 h-6",
    md: "w-8 h-8",
    lg: "w-12 h-12",
    xl: "w-20 h-20",
  };

  const containerClasses = `${sizeClasses[size]} ${className}`;
  const iconClasses = iconSizeClasses[size];

  // Priority: imageUrl -> iconUrl -> mapped icon -> local placeholder
  const preferredSrc = useMemo(() => {
    return imageUrl || iconUrl || mappedIcon || "/placeholder.svg";
  }, [imageUrl, iconUrl, mappedIcon]);

  const [resolvedSrc, setResolvedSrc] = useState(preferredSrc);

  useEffect(() => {
    setResolvedSrc(preferredSrc);
  }, [preferredSrc]);

  return (
    <Image
      src={resolvedSrc}
      alt={mappedName}
      width={600}
      height={500}
      className={className || `${containerClasses} object-contain`}
      style={style}
      onClick={onClick}
      onError={() => {
        if (resolvedSrc !== "/placeholder.svg") {
          setResolvedSrc(iconUrl || mappedIcon || "/placeholder.svg");
        }
      }}
    />
  );
};

export default Banner;
